/**
 * ASSIST-05 — the Purchase Room's RPCs.
 *
 * PostgREST through `apiRpc` with the caller's token from
 * `getValidAccessToken()` (never supabase-js; brief section 5). Every function
 * resolves; none throws. Parsing lives in components/assist/room/roomModel.ts,
 * where `tsx` tests can reach it.
 */
import { apiRpc } from './client';
import { getValidAccessToken } from './authApi';
import {
  isCaseId,
  parseActiveCases,
  parsePurchaseRoom,
  type ActiveCase,
  type PurchaseRoom,
} from '../../components/assist/room/roomModel';

/**
 * `get_purchase_room(case_id)`. `assist_case_gate` decides who may read it
 * (R2a.2: any participant, whatever the mode). A refusal, an error and an
 * unreadable answer all come back as `null`: the room shows NotFound and never
 * confirms that a case exists.
 */
export async function fetchPurchaseRoom(caseId: string): Promise<PurchaseRoom | null> {
  if (!isCaseId(caseId)) return null;
  try {
    const token = await getValidAccessToken();
    if (!token) return null;
    const { data, error } = await apiRpc<unknown>('get_purchase_room', { case_id: caseId }, token);
    if (error) return null;
    return parsePurchaseRoom(data);
  } catch {
    return null;
  }
}

/**
 * `get_my_active_case()`: the signed-in user's open cases as buyer, newest
 * first. No mode gate (R2a.2). Guests, failures and the sales demo's
 * unresolvable backend all read as no cases.
 */
export async function fetchMyActiveCases(): Promise<ActiveCase[]> {
  try {
    const token = await getValidAccessToken();
    if (!token) return [];
    const { data, error } = await apiRpc<unknown>('get_my_active_case', {}, token);
    if (error) return [];
    return parseActiveCases(data);
  } catch {
    return [];
  }
}
